import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useAuthenticatedFileUrl } from "../../../hooks/useAuthenticatedFileUrl";
import {
  getLoaderColor,
  getPlayButtonStyles,
  getPlayerLabelStyles,
  playerRowStyles,
} from "./styles";

type PlaybackOwner = {
  pause: () => void;
};

export type AsyncRoleplayPlaybackState = {
  currentTimeSec: number;
  durationSec: number;
  isPlaying: boolean;
};

let activePlaybackOwner: PlaybackOwner | null = null;

const claimPlayback = (owner: PlaybackOwner) => {
  if (activePlaybackOwner && activePlaybackOwner !== owner) {
    activePlaybackOwner.pause();
  }
  activePlaybackOwner = owner;
};

const releasePlayback = (owner: PlaybackOwner) => {
  if (activePlaybackOwner === owner) {
    activePlaybackOwner = null;
  }
};

const readDurationSec = (audio: HTMLAudioElement | null): number => {
  if (!audio) return 0;
  const { duration } = audio;
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return duration;
};

interface AsyncRoleplayAudioPlayerProps {
  audioUrl: string | null | undefined;
  label?: string;
  autoPlay?: boolean;
  variant?: "user" | "assistant";
  onPlaybackChange?: (state: AsyncRoleplayPlaybackState) => void;
}

export const AsyncRoleplayAudioPlayer: React.FC<
  AsyncRoleplayAudioPlayerProps
> = ({
  audioUrl,
  label,
  autoPlay = false,
  variant = "assistant",
  onPlaybackChange,
}) => {
  const theme = useTheme();
  const { url, isLoading, error } = useAuthenticatedFileUrl(audioUrl);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const unbindRef = useRef<(() => void) | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const autoPlayedRef = useRef<string | null>(null);
  const knownDurationRef = useRef(0);
  const ownerRef = useRef<PlaybackOwner>({ pause: () => undefined });
  const onPlaybackChangeRef = useRef(onPlaybackChange);
  onPlaybackChangeRef.current = onPlaybackChange;

  const emitPlayback = (
    audio: HTMLAudioElement | null,
    playing: boolean,
  ) => {
    const reported = readDurationSec(audio);
    if (reported > knownDurationRef.current) {
      knownDurationRef.current = reported;
    }
    // MediaRecorder/webm often under-reports duration; grow with playhead.
    if (audio && playing) {
      knownDurationRef.current = Math.max(
        knownDurationRef.current,
        audio.currentTime,
      );
    }
    if (audio && audio.ended) {
      knownDurationRef.current = Math.max(
        knownDurationRef.current,
        audio.currentTime,
        reported,
      );
    }

    onPlaybackChangeRef.current?.({
      currentTimeSec: audio ? audio.currentTime : 0,
      durationSec: knownDurationRef.current,
      isPlaying: playing,
    });
  };

  const stopPlayback = (opts?: { resetTime?: boolean }) => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      if (opts?.resetTime) {
        audio.currentTime = 0;
      }
    }
    setIsPlaying(false);
    emitPlayback(audio, false);
    releasePlayback(ownerRef.current);
  };

  ownerRef.current.pause = () => {
    stopPlayback();
  };

  const bindAudioEvents = (audio: HTMLAudioElement) => {
    unbindRef.current?.();

    const onTimeUpdate = () => emitPlayback(audio, !audio.paused);
    const onLoaded = () => {
      const reported = readDurationSec(audio);
      if (reported > knownDurationRef.current) {
        knownDurationRef.current = reported;
      }
      emitPlayback(audio, !audio.paused);
    };
    const onEnded = () => {
      setIsPlaying(false);
      knownDurationRef.current = Math.max(
        knownDurationRef.current,
        audio.currentTime,
        readDurationSec(audio),
      );
      emitPlayback(audio, false);
      releasePlayback(ownerRef.current);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("durationchange", onLoaded);
    audio.addEventListener("ended", onEnded);

    unbindRef.current = () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("durationchange", onLoaded);
      audio.removeEventListener("ended", onEnded);
    };
  };

  const ensureAudio = (mediaUrl: string): HTMLAudioElement => {
    const existing = audioRef.current;
    if (existing && existing.dataset.sourceUrl === mediaUrl) {
      return existing;
    }

    if (existing) {
      existing.pause();
      unbindRef.current?.();
      unbindRef.current = null;
    }

    knownDurationRef.current = 0;
    const audio = new Audio(mediaUrl);
    audio.preload = "auto";
    audio.dataset.sourceUrl = mediaUrl;
    audioRef.current = audio;
    bindAudioEvents(audio);
    return audio;
  };

  useEffect(() => {
    return () => {
      unbindRef.current?.();
      unbindRef.current = null;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      releasePlayback(ownerRef.current);
    };
  }, []);

  // One-shot autoplay — do not pause on dependency flips (that cut audio short).
  useEffect(() => {
    if (!url || !autoPlay) return;
    if (autoPlayedRef.current === url) return;
    autoPlayedRef.current = url;

    const owner = ownerRef.current;
    const audio = ensureAudio(url);
    claimPlayback(owner);
    void audio
      .play()
      .then(() => {
        setIsPlaying(true);
        emitPlayback(audio, true);
      })
      .catch(() => {
        setIsPlaying(false);
        emitPlayback(audio, false);
        releasePlayback(owner);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, autoPlay]);

  const togglePlay = async () => {
    if (!url) return;

    const owner = ownerRef.current;
    const audio = ensureAudio(url);

    if (isPlaying) {
      stopPlayback();
      return;
    }

    claimPlayback(owner);

    try {
      await audio.play();
      setIsPlaying(true);
      emitPlayback(audio, true);
    } catch (playError) {
      console.error("Failed to play async roleplay audio:", playError);
      setIsPlaying(false);
      emitPlayback(audio, false);
      releasePlayback(owner);
    }
  };

  if (!audioUrl) return null;

  return (
    <Box sx={playerRowStyles}>
      {isLoading ? (
        <CircularProgress
          size={18}
          sx={{ color: getLoaderColor(theme, variant) }}
        />
      ) : (
        <IconButton
          size="small"
          onClick={() => void togglePlay()}
          disabled={!!error || !url}
          sx={getPlayButtonStyles(variant)}
        >
          {isPlaying ? (
            <PauseIcon fontSize="small" />
          ) : (
            <PlayArrowIcon fontSize="small" />
          )}
        </IconButton>
      )}
      <Typography variant="caption" sx={getPlayerLabelStyles(variant)}>
        {error ? "Audio unavailable" : label || "Play message"}
      </Typography>
    </Box>
  );
};
